import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { TeamIcon } from 'common/react/components/TeamIcon';
import { getTranslation } from 'common/react/utils/translations';
import { Loader } from 'common/react/components/Loader';
import { EmptyState } from 'common/react/components/EmptyState';
import { RoundVotes } from './round-votes';
import { TeamWatermarkBackground } from 'common/react/components/TeamWatermarkBackground';
import { PlayerPhoto } from './PlayerPhoto';
import { useLeaderboardDataWithPredictedVotes } from './hooks/use-leaderboard-with-predictor-data';

const getTeamProviderId = (teamName = '') =>
    teamProviderIds[teamName.replace('aflc-', '')];

const teamProviderIds = {
    adel: 'CD_T10',
    bl: 'CD_T20',
    carl: 'CD_T30',
    coll: 'CD_T40',
    ess: 'CD_T50',
    fre: 'CD_T60',
    gcfc: 'CD_T1000',
    geel: 'CD_T70',
    gws: 'CD_T1010',
    haw: 'CD_T80',
    melb: 'CD_T90',
    nmfc: 'CD_T100',
    port: 'CD_T110',
    rich: 'CD_T120',
    stk: 'CD_T130',
    syd: 'CD_T160',
    wb: 'CD_T140',
    wce: 'CD_T150'
};

export const LeadderboardEmbeddable = ({
    title,
    subtitle,
    linkTo,
    linkToLabel,
    seasonPid,
    seasonId,
    competitionId,
    numberOfPlayers,
    clubName
}) => {
    const isWebView =
        PULSE.core.url.getParam('webview') === 'true'
            ? true
            : window.IS_ARTICLE_API === true;
    const [noDataAvailable, setNoDataAvailable] = useState(false);

    const clubPid = getTeamProviderId(clubName);
    const clubFilter =
        PULSE.app.common.CONSTANTS.TEAM_ABBR[clubPid]?.toLowerCase();

    const { data, rounds, isLoading } = useLeaderboardDataWithPredictedVotes({
        competitionId: competitionId,
        compSeasonId: seasonId,
        compSeasonPid: seasonPid,
        pageSize: numberOfPlayers,
        clubFilter: clubFilter
    });

    useEffect(() => {
        // Set no data available to show the empty state
        if (!isLoading && data.leaderboard?.length === 0) {
            setNoDataAvailable(true);
        } else {
            setNoDataAvailable(false);
        }
    }, [data, data?.status, isLoading]);

    const isAFLW = PULSE.app.common.match.getCompAbbr(seasonPid) === 'AFLW';

    return (
        <div
            className={`${
                isAFLW
                    ? 'brownlow-embeddable__aflw-theme'
                    : 'brownlow-embeddable__aflm-theme'
            } ${isWebView ? 'brownlow-embeddable--static-dimensions' : ''}`}
        >
            <div className="brownlow-embeddable__header">
                {clubFilter && (
                    <>
                        <TeamWatermarkBackground
                            teamAbbreviation={clubFilter}
                            className="stats-card-featured-player__bg stats-card-featured-player__bg--no-border-radius"
                            side="right"
                            providerId={clubPid}
                        />
                        <TeamIcon
                            className={'icon brownlow-embeddable__club-icon'}
                            providerId={clubPid}
                            theme={PULSE.app.common.team.getTeamLogoTheme(
                                clubFilter,
                                'b'
                            )}
                        />
                    </>
                )}
                <div
                    className={`brownlow-embeddable__logo ${
                        isAFLW ? 'brownlow-embeddable__logo-aflw' : ''
                    }`}
                ></div>
                <div className="brownlow-embeddable__header-text-container">
                    <h3 className="brownlow-embeddable__title">
                        {title ?? ''}
                    </h3>
                    <p className="brownlow-embeddable__subtitle">
                        {subtitle ?? ''}
                    </p>
                </div>
            </div>

            {isLoading && (
                <div className="brownlow-leaderboard-embeddable__loader">
                    <Loader />
                </div>
            )}

            {noDataAvailable && (
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation=""
                />
            )}

            {data?.leaderboard?.length > 0 &&
                !noDataAvailable &&
                !isLoading && (
                    <table
                        className={`brownlow-leaderboard-embeddable__table ${
                            isAFLW
                                ? 'brownlow-leaderboard-embeddable__table--aflw'
                                : ''
                        }`}
                    >
                        <tbody>
                            {data?.leaderboard
                                ?.slice(0, numberOfPlayers)
                                .map((item) => (
                                    <tr
                                        key={`brownlow-embeddable-${item.player.playerId}`}
                                        className={`brownlow-leaderboard-embeddable__row ${
                                            item.player.eligible === false
                                                ? 'brownlow-leaderboard-embeddable__row--ineligible'
                                                : ''
                                        }`}
                                    >
                                        <td
                                            className={`brownlow-leaderboard-embeddable__cell brownlow-leaderboard-embeddable__cell--name ${
                                                isAFLW
                                                    ? 'brownlow-leaderboard-embeddable__cell--name__aflw'
                                                    : ''
                                            }`}
                                        >
                                            <div className="brownlow-leaderboard-embeddable__name-block">
                                                <div className="brownlow-leaderboard-embeddable__left_side">
                                                    <div className="brownlow-leaderboard-embeddable__left-side-wrapper">
                                                        <PlayerPhoto
                                                            className={
                                                                'brownlow-leaderboard-embeddable__photo'
                                                            }
                                                            photoUrl={
                                                                item.player
                                                                    .photoURL
                                                            }
                                                            scale={'0.1'}
                                                            alt={`${getTranslation(
                                                                'label.stats.headshot.alt'
                                                            )} ${
                                                                item.player
                                                                    .givenName
                                                            } ${
                                                                item.player
                                                                    .surname
                                                            }`}
                                                        />
                                                        <div className="brownlow-leaderboard-embeddable__name-wrapper">
                                                            <span className="brownlow-leaderboard-embeddable__name">
                                                                {
                                                                    item.player
                                                                        .givenName
                                                                }{' '}
                                                                {
                                                                    item.player
                                                                        .surname
                                                                }
                                                            </span>
                                                            {item.player
                                                                .eligible ===
                                                            false ? (
                                                                <span className="brownlow-leaderboard-embeddable__ineligible">
                                                                    {getTranslation(
                                                                        'label.brownlow.predictor.ineligible'
                                                                    )}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="brownlow-leaderboard-embeddable__right_side">
                                                    {item.player.winner ? (
                                                        <div
                                                            className={`brownlow-leaderboard-embeddable__winner-badge ${
                                                                isAFLW
                                                                    ? 'brownlow-leaderboard-embeddable__winner-badge-aflw'
                                                                    : ''
                                                            }`}
                                                        ></div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            className={`brownlow-leaderboard-embeddable__cell brownlow-leaderboard-embeddable__cell--rounds 
                                            ${
                                                isAFLW
                                                    ? 'brownlow-leaderboard-embeddable__cell--rounds__aflw'
                                                    : ''
                                            }`}
                                        >
                                            <RoundVotes
                                                data={item}
                                                rounds={rounds}
                                            />
                                        </td>
                                        <td
                                            className={`brownlow-leaderboard-embeddable__cell brownlow-leaderboard-embeddable__cell--total-votes ${
                                                isAFLW
                                                    ? 'brownlow-leaderboard-embeddable__cell--total-votes__aflw'
                                                    : ''
                                            }`}
                                        >
                                            {item.totalVotes ?? '0'}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}

            {linkTo && linkToLabel && (
                <div className="brownlow-embeddable__footer">
                    <a
                        href={linkTo}
                        className="brownlow-embeddable__footer-link"
                    >
                        {linkToLabel}
                        <SvgIcon icon="arrow-right" className="icon" />
                    </a>
                </div>
            )}
        </div>
    );
};

LeadderboardEmbeddable.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    linkTo: PropTypes.string,
    linkToLabel: PropTypes.string,
    seasonPid: PropTypes.string.isRequired,
    seasonId: PropTypes.string.isRequired,
    numberOfPlayers: PropTypes.string.isRequired,
    clubName: PropTypes.string,
    competitionId: PropTypes.string
};
